import { Component, DestroyRef, Inject, inject, OnInit, signal, WritableSignal } from '@angular/core';
import { AbstractControl, FormArray, FormGroup } from '@angular/forms';
import { MessageService } from 'primeng/api';
import { ButtonModule } from 'primeng/button';
import { DialogModule } from 'primeng/dialog';
import { DialogService, DynamicDialogRef } from 'primeng/dynamicdialog';
import { HandlersService } from 'src/app/api';
import { DialogData } from 'src/app/api/model/dialogData';
import { AppResource } from 'src/app/app.resource';
import { CombinedState, CombinedStore } from 'src/app/core/combined.store';
import { Resource } from 'src/app/resources/resource';
import { VyvPopupComponent, VyvPopupConfig } from 'src/app/shared/components/vyv-popup/vyv-popup.component';
import { CANAL_VENTE_C } from 'src/app/shared/constants/context.constants';
import { PathType } from 'src/app/shared/enum/path-type.enum';
import { Store } from 'src/app/shared/enum/store.enum';
import { NavigationService } from 'src/app/shared/services/navigation.service';
import { VyvToastService } from 'src/app/shared/services/toast.service';
import { VyvDialogService } from 'src/app/shared/services/vyv-dialog.service';
import { isMetropolitanPath } from 'src/app/shared/utils/path-type-utils';

@Component({
  selector: 'app-base',
  templateUrl: './base.component.html',
  standalone: true,
  providers: [DialogModule, ButtonModule, MessageService],
  imports: [DialogModule],
})
export abstract class BaseComponent implements OnInit {
  public resource!: Resource;
  public store = inject(CombinedStore);
  public form!: FormGroup;
  public submitted = false;
  public popupReference: DynamicDialogRef | undefined;
  public currentPathType: PathType = this.store.path();
  public endOfWorkflow: boolean = false;
  protected destroyRef = inject(DestroyRef);
  protected dialogService = inject(DialogService);
  protected handlerService = inject(HandlersService);
  protected messageService = inject(VyvToastService);
  protected vyvDialogService = inject(VyvDialogService);
  PathType = PathType;
  isLoading: WritableSignal<boolean> = signal(false);
  isCanalAdvisor: boolean = false;
  pathType = PathType;
  constructor(
    protected resources: AppResource,
    protected navigationService?: NavigationService,
    @Inject('storeName') public storeName?: string,
  ) {
    this.resource = resources['resource'];
    this.storeName = storeName ?? Store.Declaration;
    this.form = new FormGroup({});
    this.store.context().canalVente === CANAL_VENTE_C ? (this.isCanalAdvisor = true) : (this.isCanalAdvisor = false);
    if (this.navigationService) this.navigationService.formSubmitted = false;
    this.scrollToTop();
  }

  ngOnInit() {}

  updateFormWithStore() {
    const controlValueStore = (this.store.select() as CombinedState)[this.storeName as keyof CombinedState];

    if (this.form !== undefined && typeof controlValueStore === 'object') {
      if (controlValueStore) {
        updateFormGroupValues(this.form, controlValueStore);
      }
      this.navigationService?.updateStepValidity(this.form.valid);
    }
  }

  isWorkflowComparator = this.store.isWorkflowFromComparator();

  scrollToTop() {
    window.scroll({
      top: 0,
      left: 0,
      behavior: 'smooth',
    });
  }

  hasError(control: AbstractControl | null): boolean {
    return (
      !!control?.invalid && (control?.dirty || control?.touched || (this.navigationService?.formSubmitted ?? false))
    );
  }

  protected isMetropolitanPath(pathType: PathType): boolean {
    return isMetropolitanPath(pathType);
  }

  displayModalError(disableForm: boolean = false, blockWorkflow: boolean = true) {
    this.popupReference = this.dialogService.open(VyvPopupComponent, {
      ...VyvPopupConfig,
      data: {
        title: this.resource.error.workflowOut.title,
        showTitle: true,
        content: [this.resource.error.workflowOut.cantContinue],
      } as DialogData,
    });

    if (blockWorkflow) this.endOfWorkflow = true;
    if (disableForm) this.form.disable({ emitEvent: false });
  }

  displayApiError() {
    this.vyvDialogService.openErrorDialogApi();
  }

  updateResourceWithTokens(resource: string, tokens: string[]): string {
    return tokens.reduce((acc, token, index) => {
      return acc.replace(`{${index}}`, token);
    }, resource);
  }
}

function updateFormGroupValues(
  formGroup: FormGroup,
  // eslint-disable-next-line @typescript-eslint/no-explicit-any
  controlValueStore: { [key: string]: any },
) {
  Object.keys(formGroup.controls).forEach((controlName) => {
    const control = formGroup.controls[controlName];
    const controlValue = controlValueStore[controlName];

    const controlEventEmit = ['account_owner'];

    if (control instanceof FormArray) {
      // Si le contrôle est un FormArray, mettre à jour chaque item de la liste
      if (Array.isArray(controlValue)) {
        control.controls.forEach((item, index) => {
          const itemValue = controlValue[index];
          if (item instanceof FormGroup && itemValue !== undefined) {
            updateFormGroupValues(item, itemValue);
          } else if (itemValue !== undefined) {
            item.setValue(itemValue, { emitEvent: false });
          }
        });
      }
    } else if (control instanceof FormGroup) {
      // Si le contrôle est un FormGroup, appeler récursivement la fonction
      updateFormGroupValues(control, controlValue || {});
    } else if (controlValue !== undefined) {
      // Sinon, c'est un FormControl, donc on peut définir la valeur
      if (controlEventEmit.includes(controlName)) {
        control?.setValue(controlValue, { emitEvent: false });
      } else {
        control?.setValue(controlValue);
      }
    }
  });
}
